/**
 * TopbarMobileMenu — content of the mobile slide-out navigation panel.
 * The panel shell (positioning, animation, backdrop) lives in Topbar.js.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ExternalLink, LinkedLogo, ListYourHorseLink, NamedLink } from '../../../../components';

import css from './TopbarMobileMenu.module.css';

// Quiet footer links folded in from the site footer drawer (Option 3).
// EMPTY for launch — every prior entry (The Market, Guidelines, Get alerts,
// Free guides, Blog, Brand assets) was a '#' placeholder; links return here
// one-by-one as their pages go live. The grid renders nothing while empty.
const FOOTER_LINKS = [];

// ── Icons ────────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3a0104" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.5" aria-hidden="true">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const GridIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);

const HeartIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
);

const UserIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
  </svg>
);

const LogOutIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
    <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const MailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2"/><polyline points="3 7 12 13 21 7"/>
  </svg>
);

const LinkIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

// ── Custom link item ──────────────────────────────────────────────────────────

const CustomLinkComponent = ({ linkConfig, currentPage, onClose }) => {
  const { text, type, href, route } = linkConfig;
  const isActive = (() => {
    if (!currentPage) return false;
    const hasName = n => currentPage.indexOf(n) === 0;
    const isCMS = id => hasName('CMSPage') && currentPage === `CMSPage:${id}`;
    const isInbox = tab => hasName('InboxPage') && currentPage === `InboxPage:${tab}`;
    return isCMS(route?.params?.pageId) || isInbox(route?.params?.tab) || currentPage === (route?.name || '');
  })();

  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    return (
      <li className={classNames(css.menuItem, { [css.menuItemActive]: isActive })}>
        <NamedLink name={name} params={params} to={to} className={css.menuItemLink} onClick={onClose}>
          <span className={css.menuItemIcon}><LinkIcon /></span>
          <span className={css.menuItemText}>{text}</span>
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={classNames(css.menuItem, { [css.menuItemActive]: isActive })}>
      <ExternalLink href={href} className={css.menuItemLink} onClick={onClose}>
        <span className={css.menuItemIcon}><LinkIcon /></span>
        <span className={css.menuItemText}>{text}</span>
      </ExternalLink>
    </li>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Mobile slide-out menu content.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {Object?} props.currentUser
 * @param {Array} props.customLinks
 * @param {Function} props.onLogout
 * @param {boolean} props.showCreateListingsLink
 * @param {Function} props.onClose
 * @returns {JSX.Element}
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUser,
    customLinks,
    onLogout,
    showCreateListingsLink,
    onClose,
  } = props;

  const currentPageClass = page => {
    const isAccountSettings = page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxMatch = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettings || isInboxMatch ? css.menuItemActive : null;
  };

  const extraLinks = customLinks.map((linkConfig, i) => (
    <CustomLinkComponent
      key={`${linkConfig.text}_${i}`}
      linkConfig={linkConfig}
      currentPage={currentPage}
      onClose={onClose}
    />
  ));

  // ── Shared: panel header ─────────────────────────────────────
  const panelHeader = (
    <div className={css.panelHeader}>
      <LinkedLogo
        layout="mobile"
        rootClassName={css.panelLogoWrap}
        logoImageClassName={css.panelLogoImg}
        alt="The Premier Horse"
      />
      <button className={css.closeBtn} onClick={onClose} aria-label="Close menu" type="button">
        <CloseIcon />
      </button>
    </div>
  );

  // ── Shared: gold "List your horse" CTA ───────────────────────
  const listCTA = (
    <div className={css.ctaWrap}>
      <ListYourHorseLink className={css.ctaBtn} onHandled={onClose}>
        <span className={css.ctaIconTile}>
          <PlusIcon />
        </span>
        <span className={css.ctaTextBlock}>
          <span className={css.ctaTitle}>List your horse</span>
          <span className={css.ctaSub}>Reach serious buyers</span>
        </span>
        <ChevronRightIcon />
        <span className={css.ctaShine} aria-hidden="true" />
      </ListYourHorseLink>
    </div>
  );

  // ── Shared: quiet small-links grid (pinned footer, above sign-in/logout) ──
  // An empty FOOTER_LINKS renders nothing — no empty grid in the footer block.
  const footerLinks =
    FOOTER_LINKS.length > 0 ? (
      <div className={css.footerLinkGrid}>
        {FOOTER_LINKS.map(({ label, href }) => (
          <a key={label} href={href} className={css.footerLink} onClick={onClose}>
            {label}
          </a>
        ))}
      </div>
    ) : null;

  // ── Shared: legal line — same destinations the site footer uses ──────────
  const legalLine = (
    <p className={css.footerMeta}>
      <NamedLink name="PrivacyPolicyPage" className={css.footerMetaLink} onClick={onClose}>
        Privacy
      </NamedLink>
      <span className={css.footerDot}>&middot;</span>
      <NamedLink name="TermsOfServicePage" className={css.footerMetaLink} onClick={onClose}>
        Terms
      </NamedLink>
      <span className={css.footerDot}>&middot;</span>
      <span>© 2026 TPH</span>
    </p>
  );

  // ── Shared: public "Contact Us" item (both auth states) ──────
  const contactItem = (
    <li className={classNames(css.menuItem, currentPageClass('ContactPage'))}>
      <NamedLink name="ContactPage" className={css.menuItemLink} onClick={onClose}>
        <span className={css.menuItemIcon}><MailIcon /></span>
        <span className={css.menuItemText}>
          <FormattedMessage id="Topbar.contactUsLink" />
        </span>
      </NamedLink>
    </li>
  );

  // ── Unauthenticated ──────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className={css.root}>
        {panelHeader}
        {listCTA}
        <nav className={css.menuBody} aria-label="Site navigation">
          <ul className={css.menuList}>{contactItem}</ul>
          {extraLinks.length > 0 && <ul className={css.menuList}>{extraLinks}</ul>}
        </nav>
        <div className={css.menuFooter}>
          {footerLinks}
          <NamedLink name="LoginPage" className={css.signInBtn} onClick={onClose}>
            Sign in or create account
          </NamedLink>
          {legalLine}
        </div>
      </div>
    );
  }

  // ── Authenticated ────────────────────────────────────────────
  const manageListingsItem = showCreateListingsLink ? (
    <li className={classNames(css.menuItem, currentPageClass('ManageListingsPage'))}>
      <NamedLink name="ManageListingsPage" className={css.menuItemLink} onClick={onClose}>
        <span className={css.menuItemIcon}><GridIcon /></span>
        <span className={css.menuItemText}>
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </span>
      </NamedLink>
    </li>
  ) : null;

  return (
    <div className={css.root}>
      {panelHeader}
      {listCTA}
      <nav className={css.menuBody} aria-label="Site navigation">
        <ul className={css.menuList}>
          {/* No Inbox item — TPH has no in-app inbox; conversations run over SMS. */}
          {manageListingsItem}
          <li className={classNames(css.menuItem, currentPageClass('FavoriteListingsPage'))}>
            <NamedLink name="FavoriteListingsPage" className={css.menuItemLink} onClick={onClose}>
              <span className={css.menuItemIcon}><HeartIcon /></span>
              <span className={css.menuItemText}>My favorites</span>
            </NamedLink>
          </li>
          <li className={classNames(css.menuItem, currentPageClass('ProfileSettingsPage'))}>
            <NamedLink name="ProfileSettingsPage" className={css.menuItemLink} onClick={onClose}>
              <span className={css.menuItemIcon}><UserIcon /></span>
              <span className={css.menuItemText}>
                <FormattedMessage id="TopbarMobileMenu.publicProfileLink" />
              </span>
            </NamedLink>
          </li>
          <li className={classNames(css.menuItem, currentPageClass('AccountSettingsPage'))}>
            <NamedLink name="AccountSettingsPage" className={css.menuItemLink} onClick={onClose}>
              <span className={css.menuItemIcon}><SettingsIcon /></span>
              <span className={css.menuItemText}>
                <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
              </span>
            </NamedLink>
          </li>
          {contactItem}
        </ul>
        {extraLinks.length > 0 && <ul className={css.menuList}>{extraLinks}</ul>}
        {/* Log out — a menu item, last in the scrollable body (not the footer) */}
        <ul className={css.menuList}>
          <li className={css.menuItem}>
            <button
              type="button"
              className={css.menuItemButton}
              onClick={() => { onLogout(); onClose(); }}
            >
              <span className={css.menuItemIcon}><LogOutIcon /></span>
              <span className={css.menuItemText}>
                <FormattedMessage id="TopbarMobileMenu.logoutLink" />
              </span>
            </button>
          </li>
        </ul>
      </nav>
      <div className={css.menuFooter}>
        {footerLinks}
        {legalLine}
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
